import { useEffect, useRef, useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { cva, VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '~/components/UI/shadcn/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/UI/shadcn/dropdown-menu'

const breadVariants = cva('', {
  variants: {
    colour: {
      'ink-blue':
        'text-blue-d [&_a]:text-blue-d [&_svg]:fill-blue-d-60 hover:[&_a]:text-blue-d dark:text-white dark:[&_svg]:fill-white dark:hover:[&_a]:text-white dark:[&_a]:text-white',
      'mid-blue':
        'text-blue-m hover:[&_a]:text-blue-m [&_svg]:fill-blue-m-60 dark:text-white dark:[&_svg]:fill-white dark:hover:[&_a]:text-white dark:[&_a]:text-white',
      white:
        'text-white [&_svg]:fill-white [&_svg]:opacity-60 hover:[&_a]:text-white [&_a]:text-white [&_a]:shadow-white'
    },
    dropdownColour: {
      'ink-blue': '',
      'mid-blue': 'text-blue-m hover:[&_a]:text-blue-m focus:text-blue-m',
      white: ''
    },
    defaultVariants: {
      colour: 'ink-blue',
      dropdownColour: 'ink-blue'
    }
  }
})

export interface BreadProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof breadVariants> {
  data: {
    items: Item[]
    current: string
  }
  className?: string
}

type Item = {
  entry: Entry
  displayName: string
  path: string
  slug: string
}

const Breadcrumbs = ({ colour, data, className }: BreadProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | undefined>(1000)
  const [openDropdown, setOpenDropdown] = useState(false)

  useEffect(() => {
    // console.log(ref.current?.clientWidth)
    setWidth(ref.current?.clientWidth)
    window.addEventListener('resize', () => setWidth(ref.current?.clientWidth))
  }, [])

  return (
    <>
      {data?.items.length > 0 && (
        <div
          ref={ref}
          className={cn(
            breadVariants({ colour: colour, dropdownColour: null }),
            className
          )}
        >
          {/* <div className='container'> */}
          <Breadcrumb>
            <BreadcrumbList className='pl-0'>
              {data?.items.length > 4 ||
              (width && width < 640 && data?.items.length > 2) ? (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      href={data?.items[0].path}
                      className='unstyled-link hover:shadow-link focus-visible:shadow-link text-xs opacity-60 hover:opacity-100'
                    >
                      {data?.items[0].displayName}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className='list-none opacity-60' />
                  <BreadcrumbItem>
                    <DropdownMenu
                      open={openDropdown}
                      onOpenChange={setOpenDropdown}
                    >
                      <div
                        className='flex items-center'
                        onMouseEnter={() => setOpenDropdown(true)}
                        onMouseLeave={() => setOpenDropdown(false)}
                      >
                        <DropdownMenuTrigger
                          onClick={() => setOpenDropdown(!openDropdown)}
                          onKeyUp={e => {
                            if (e.key === 'Enter')
                              setOpenDropdown(!openDropdown)
                          }}
                          aria-label='More links'
                        >
                          <BreadcrumbEllipsis
                            className={cn(
                              openDropdown ? 'opacity-100' : 'opacity-40'
                            )}
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align='start'
                          className='bg-grey-l rounded-none border-none p-0! shadow-none'
                        >
                          {data?.items.map(
                            (item: Item, index: Index) =>
                              index > 0 &&
                              index < data.items.length - 1 && (
                                <DropdownMenuItem
                                  key={index}
                                  className={cn(
                                    breadVariants({
                                      colour: null,
                                      dropdownColour: colour
                                    }),
                                    item.entry
                                      ? 'group focus:bg-blue-l-20 cursor-pointer p-0 transition-colors'
                                      : ''
                                  )}
                                  asChild
                                >
                                  <BreadcrumbLink
                                    {...(item.entry
                                      ? {
                                          href: item.path
                                        }
                                      : {})}
                                    className='unstyled-link h-full w-full p-[12px]! text-xs'
                                  >
                                    <span
                                      className={
                                        item.entry
                                          ? 'group-hover:shadow-link group-focus:shadow-link'
                                          : ''
                                      }
                                    >
                                      {item.displayName}
                                    </span>
                                  </BreadcrumbLink>
                                </DropdownMenuItem>
                              )
                          )}
                        </DropdownMenuContent>
                      </div>
                    </DropdownMenu>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className='list-none opacity-60' />
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      {...(data?.items[data.items.length - 1].entry
                        ? { href: data?.items[data.items.length - 1].path }
                        : {})}
                      className={cn(
                        'unstyled-link text-xs opacity-80',
                        data?.items[data.items.length - 1].entry
                          ? 'hover:shadow-link focus-visible:shadow-link hover:opacity-100'
                          : ''
                      )}
                    >
                      {data?.items[data.items.length - 1].displayName}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className='list-none opacity-60' />
                </>
              ) : (
                data?.items.map((item: Item, index: Index) => (
                  <Fragment key={index}>
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        {...(item.entry ? { href: item.path } : {})}
                        className={cn(
                          'unstyled-link text-xs opacity-80',
                          item.entry
                            ? 'hover:shadow-link focus-visible:shadow-link hover:opacity-100'
                            : ''
                        )}
                      >
                        {item.displayName}
                      </BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbSeparator className='list-none opacity-60' />
                  </Fragment>
                ))
              )}
              <BreadcrumbItem>
                <BreadcrumbPage className='text-xs'>
                  {data?.current}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        // </section>
      )}
    </>
  )
}

export default Breadcrumbs
